import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Search, Sparkles, ChevronDown, FlaskConical, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.png?w=154&format=webp&quality=85";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useSiteContent } from "@/hooks/useSiteContent";

const AIChatDialog = lazy(() => import("@/components/AIChatDialog"));
const QuizDialog = lazy(() => import("@/components/QuizDialog"));

const defaultNavLinks = [
  { label: "Home", href: "#hero" },
  { label: "Warum wir", href: "#zasto-mi" },
  { label: "Ergebnisse", href: "#rezultati" },
  { label: "Erfahrungen", href: "#iskustva" },
  { label: "Kontakt", href: "#kontakt" },
];

const defaultProgramLinks = [
  { label: "Numicon", href: "/programi/numicon" },
  { label: "Mentale Arithmetik", href: "/programi/mentalna-aritmetika" },
  { label: "Kleiner Alleswisser", href: "/programi/kleiner-alleswisser" },
  { label: "Programmieren für Kinder", href: "/programi/programmieren" },
];

const Navbar = () => {
  const { data: siteContent } = useSiteContent();
  const navData = siteContent?.nav_items;
  const navLinks = navData?.items?.length ? navData.items : defaultNavLinks;
  const programLinks = navData?.program_items?.length ? navData.program_items : defaultProgramLinks;
  const ctaText = navData?.cta_text || "Anmeldung";

  const { data: cmsPages } = useQuery({
    queryKey: ["cms-nav-pages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_pages")
        .select("title, slug, page_type")
        .eq("is_published", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setProgramsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDropdownEnter = () => { clearTimeout(timeoutRef.current); setProgramsOpen(true); };
  const handleDropdownLeave = () => { timeoutRef.current = setTimeout(() => setProgramsOpen(false), 200); };

  return (
    <>
      <motion.header
        initial={{ y: -100 }} animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-card/95 backdrop-blur-md shadow-card" : "bg-card/85 backdrop-blur-md shadow-sm"}`}
      >
        <div className="container flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex items-center gap-2">
            <img src={logo} alt="Kleines Genie Logo" className="h-12 md:h-14 w-auto" width={154} height={84} />
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 1).map((link: any) => (
              <button key={link.href} onClick={() => scrollTo(link.href)} className="px-3 py-2 text-sm font-body font-bold text-foreground drop-shadow-sm hover:text-primary rounded-lg transition-colors">
                {link.label}
              </button>
            ))}

            <div ref={dropdownRef} className="relative" onMouseEnter={handleDropdownEnter} onMouseLeave={handleDropdownLeave}>
              <button onClick={() => scrollTo("#programi")} className="px-3 py-2 text-sm font-body font-bold text-foreground drop-shadow-sm hover:text-primary rounded-lg transition-colors flex items-center gap-1">
                Programme
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${programsOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {programsOpen && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }} className="absolute top-full left-0 mt-1 bg-card rounded-xl shadow-card-hover border border-border/50 py-2 min-w-[220px] z-50">
                    <button onClick={() => { setProgramsOpen(false); scrollTo("#programi"); }} className="w-full text-left px-4 py-2.5 text-sm font-body font-bold text-primary hover:bg-muted/50 transition-colors">Alle Programme</button>
                    <div className="h-px bg-border mx-3 my-1" />
                    {programLinks.map((link: any) => (
                      <button key={link.href} onClick={() => { setProgramsOpen(false); navigate(link.href); }} className="w-full text-left px-4 py-2.5 text-sm font-body font-semibold text-foreground hover:text-primary hover:bg-muted/50 transition-colors">{link.label}</button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(1).map((link: any) => (
              <button key={link.href} onClick={() => scrollTo(link.href)} className="px-3 py-2 text-sm font-body font-bold text-foreground drop-shadow-sm hover:text-primary rounded-lg transition-colors">{link.label}</button>
            ))}

            <button onClick={() => navigate("/istrazivanja")} className="px-3 py-2 text-sm font-body font-bold text-foreground hover:text-primary rounded-lg transition-colors flex items-center gap-1.5">
              <FlaskConical className="w-4 h-4" /> Forschung
            </button>

            {cmsPages?.map((p: any) => (
              <button key={p.slug} onClick={() => navigate(`/stranica/${p.slug}`)} className="px-3 py-2 text-sm font-body font-bold text-foreground hover:text-primary rounded-lg transition-colors flex items-center gap-1.5">
                <FileText className="w-4 h-4" /> {p.title}
              </button>
            ))}

            <button onClick={() => setChatOpen(true)} className="px-3 py-2 text-sm font-body font-bold text-secondary hover:text-secondary/80 rounded-lg transition-colors flex items-center gap-1.5" title="Informationssuche über die Schule">
              <Search className="w-4 h-4" /> Suche
            </button>

            <button onClick={() => setQuizOpen(true)} className="px-3 py-2 text-sm font-body font-bold text-accent hover:text-accent/80 rounded-lg transition-colors flex items-center gap-1.5" title="Online-Test">
              <Sparkles className="w-4 h-4" /> Online-Test
            </button>

            <LanguageSwitcher />

            <Button onClick={() => scrollTo("#kontakt")} className="ml-2 bg-gradient-hero text-primary-foreground font-body font-bold shadow-card hover:shadow-card-hover transition-shadow">
              <Phone className="w-4 h-4 mr-2" /> {ctaText}
            </Button>
          </nav>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-foreground" aria-label="Menü umschalten">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-card/98 backdrop-blur-md border-t border-border">
              <div className="container py-4 flex flex-col gap-2">
                {navLinks.slice(0, 1).map((link: any) => (
                  <button key={link.href} onClick={() => scrollTo(link.href)} className="py-3 text-left font-body font-semibold text-foreground hover:text-primary transition-colors">{link.label}</button>
                ))}

                <div>
                  <button onClick={() => setMobileProgramsOpen(!mobileProgramsOpen)} className="py-3 text-left font-body font-semibold text-foreground hover:text-primary transition-colors flex items-center justify-between w-full">
                    Programme
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileProgramsOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mobileProgramsOpen && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="pl-4 flex flex-col gap-1 overflow-hidden">
                        <button onClick={() => { setMobileOpen(false); scrollTo("#programi"); }} className="py-2 text-left font-body font-semibold text-primary text-sm">Alle Programme</button>
                        {programLinks.map((link: any) => (
                          <button key={link.href} onClick={() => { setMobileOpen(false); navigate(link.href); }} className="py-2 text-left font-body font-medium text-muted-foreground hover:text-primary text-sm transition-colors">{link.label}</button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {navLinks.slice(1).map((link: any) => (
                  <button key={link.href} onClick={() => scrollTo(link.href)} className="py-3 text-left font-body font-semibold text-foreground hover:text-primary transition-colors">{link.label}</button>
                ))}

                <button onClick={() => { setMobileOpen(false); navigate("/istrazivanja"); }} className="py-3 text-left font-body font-semibold text-foreground hover:text-primary transition-colors flex items-center gap-2">
                  <FlaskConical className="w-4 h-4" /> Forschung
                </button>
                {cmsPages?.map((p: any) => (
                  <button key={p.slug} onClick={() => { setMobileOpen(false); navigate(`/stranica/${p.slug}`); }} className="py-3 text-left font-body font-semibold text-foreground hover:text-primary transition-colors flex items-center gap-2">
                    <FileText className="w-4 h-4" /> {p.title}
                  </button>
                ))}
                <button onClick={() => { setMobileOpen(false); setChatOpen(true); }} className="py-3 text-left font-body font-semibold text-secondary hover:text-secondary/80 transition-colors flex items-center gap-2">
                  <Search className="w-4 h-4" /> Informationssuche
                </button>
                <button onClick={() => { setMobileOpen(false); setQuizOpen(true); }} className="py-3 text-left font-body font-semibold text-accent hover:text-accent/80 transition-colors flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Online-Test
                </button>
                <LanguageSwitcher mobile />
                <Button onClick={() => scrollTo("#kontakt")} className="mt-2 bg-gradient-hero text-primary-foreground font-body font-bold">
                  {ctaText}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {chatOpen && (
        <Suspense fallback={null}>
          <AIChatDialog open={chatOpen} onClose={() => setChatOpen(false)} />
        </Suspense>
      )}
      {quizOpen && (
        <Suspense fallback={null}>
          <QuizDialog open={quizOpen} onClose={() => setQuizOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default Navbar;
